import React, { useCallback, useState } from "react";
import { toast } from "react-toastify";

import Modal from "../ui/Modal";
import PropertyForm from "./PropertyForm";
import PropertyMetaForm from "./PropertyMetaForm";
import { usePropertyStore } from "../../store/propertyStore";
import {
  useCreatePropertyMutation,
  useUpdatePropertyMutation,
  useExtractPdfDataMutation,
  useExtractFloorPlanDataMutation,
} from "../../queries/properties/usePropertiesQuery";
import {
  getPropertyMetaErrorMessage,
  useCreatePropertyMetaMutation,
} from "../../queries/properties/usePropertyMetaQuery";
import { SHOW_VOICE_AND_META } from "../../config/features";

const getCreatedPropertyId = (created) =>
  created?.id ?? created?.property_id ?? created?.propertyId ?? null;

const getCreatedPropertyName = (created, payload) => {
  const fromCreated = created?.title ?? created?.name;
  if (fromCreated) return fromCreated;
  if (payload instanceof FormData) return payload.get("title") || "";
  return payload?.title ?? payload?.name ?? "";
};

export default function PropertyFormModal({ agentId }) {
  const [isBrochureExtractDataOpen, setIsBrochureExtractDataOpen] =
    useState(false);
  const [isFloorPlanExtractDataOpen, setIsFloorPlanExtractDataOpen] =
    useState(false);
  const [createdProperty, setCreatedProperty] = useState(null);

  const {
    selectedProperty,
    clearSelectedProperty,
    setExtractedPdfData,
    setExtractedFloorPlanData,
  } = usePropertyStore();

  const createMutation = useCreatePropertyMutation();
  const updateMutation = useUpdatePropertyMutation();
  const createMetaMutation = useCreatePropertyMetaMutation();

  const { mutate: extractPdfData, isPending: isExtractingPdfData } =
    useExtractPdfDataMutation();
  const { mutate: extractFloorPlanData, isPending: isFloorPlanExtracting } =
    useExtractFloorPlanDataMutation();

  const isEdit = !!selectedProperty?.id;
  const resolvedAgentId =
    agentId || selectedProperty?.agent_id || selectedProperty?.agentId;

  const isMetaStep = !!createdProperty?.id;

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    createMetaMutation.isPending ||
    isExtractingPdfData ||
    isFloorPlanExtracting;

  const handleClose = useCallback(() => {
    if (createMutation.isPending || createMetaMutation.isPending) return;
    setCreatedProperty(null);
    clearSelectedProperty();
  }, [clearSelectedProperty, createMetaMutation.isPending, createMutation.isPending]);

  const handleExtractPdfData = (BrochureFile) => {
    if (!BrochureFile) return;

    if (!(BrochureFile instanceof File)) {
      toast.error("Please Reupload the PDF file for extraction");
      return;
    }
    const formData = new FormData();
    formData.append("file", BrochureFile);
    extractPdfData(formData, {
      onSuccess: (data) => {
        setExtractedPdfData(data);
        setIsBrochureExtractDataOpen(true);
      },
      onError: (error) => {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || "Failed to extract PDF data";
        toast.error(errorMessage);
      },
    });
  };

  const handleExtractFloorPlanData = (FloorPlanFile) => {
    if (!FloorPlanFile) return;

    if (!(FloorPlanFile instanceof File)) {
      toast.error("Please Reupload the file for extraction");
      return;
    }
    const formData = new FormData();
    formData.append("file", FloorPlanFile);
    extractFloorPlanData(formData, {
      onSuccess: (data) => {
        setExtractedFloorPlanData(data);
        setIsFloorPlanExtractDataOpen(true);
      },
      onError: (error) => {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || "Failed to extract Floor Plan data";
        toast.error(errorMessage);
      },
    });
  };

  const handleSubmit = (payload) => {
    if (!resolvedAgentId) {
      toast.error("Agent is required to save this property");
      return;
    }

    if (isEdit) {
      updateMutation.mutate(
        {
          agentId: resolvedAgentId,
          propertyId: selectedProperty.id,
          data: payload,
        },
        {
          onSuccess: () => {
            toast.success("Property updated successfully");
            setCreatedProperty(null);
            clearSelectedProperty();
          },
          onError: (error) => {
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              "Failed to update property";
            toast.error(errorMessage);
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          agentId: resolvedAgentId,
          data: payload,
        },
        {
          onSuccess: (created) => {
            toast.success("Property created successfully");
            if (!SHOW_VOICE_AND_META) {
              setCreatedProperty(null);
              clearSelectedProperty();
              return;
            }
            const propertyId = getCreatedPropertyId(created);
            if (!propertyId) {
              toast.error(
                "Could not open conversation limits. Property id is missing.",
              );
              setCreatedProperty(null);
              clearSelectedProperty();
              return;
            }

            setCreatedProperty({
              id: propertyId,
              name: getCreatedPropertyName(created, payload),
            });
          },
          onError: (error) => {
            const errorMessage =
              error.response?.data?.message ||
              error.message ||
              "Failed to create property";
            toast.error(errorMessage);
          },
        },
      );
    }
  };

  const handleMetaSubmit = (data) => {
    if (!resolvedAgentId || !createdProperty?.id) {
      toast.error("Property is required to save conversation limits");
      return;
    }

    createMetaMutation.mutate(
      {
        agentId: resolvedAgentId,
        propertyId: createdProperty.id,
        data,
      },
      {
        onSuccess: () => {
          toast.success("Conversation limits saved");
          setCreatedProperty(null);
          clearSelectedProperty();
        },
        onError: (error) => {
          toast.error(
            getPropertyMetaErrorMessage(
              error,
              "Failed to save conversation limits",
            ),
          );
        },
      },
    );
  };

  return (
    <Modal
      isOpen={!!selectedProperty}
      onClose={handleClose}
      className="property-form-modal"
      title={
        isMetaStep
          ? "Conversation Limits"
          : isEdit
            ? "Edit Property"
            : "Add Property"
      }
    >
      {selectedProperty &&
        (SHOW_VOICE_AND_META && isMetaStep ? (
          <PropertyMetaForm
            propertyName={createdProperty.name}
            isLoading={createMetaMutation.isPending}
            onSubmit={handleMetaSubmit}
            onCancel={handleClose}
          />
        ) : (
          <PropertyForm
            setIsBrochureExtractDataOpen={setIsBrochureExtractDataOpen}
            handleExtractPdfData={handleExtractPdfData}
            isBrochureExtractDataOpen={isBrochureExtractDataOpen}
            isFloorPlanExtractDataOpen={isFloorPlanExtractDataOpen}
            setIsFloorPlanExtractDataOpen={setIsFloorPlanExtractDataOpen}
            onCloseBrochureExtractData={() => setIsBrochureExtractDataOpen(false)}
            onCloseFloorPlanExtractData={() =>
              setIsFloorPlanExtractDataOpen(false)
            }
            isFloorPlanExtracting={isFloorPlanExtracting}
            initialData={selectedProperty || {}}
            agentId={resolvedAgentId}
            handleExtractFloorPlanData={handleExtractFloorPlanData}
            onSubmit={handleSubmit}
            isPDFExtracting={isExtractingPdfData}
            isLoading={isPending}
            onCancel={handleClose}
          />
        ))}
    </Modal>
  );
}
